import { useMutation } from '@apollo/client';
import React, { FC, useCallback, useState } from 'react';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { FormFooter, FormHeader } from '@atlaskit/form';
import { Task, UpdateStatus } from '@css-xp/apd-common';
import { useApplicationContext } from '@css-xp/base/common-context';
import { SlackChannelLink } from '../common';
import { ModalTitle } from '../constants/modal-titles';
import { assignTask as ASSIGN_TASK } from '../queries/tasks';
import ErrorNotificationMessage from './error-notification-message';
import './modal.scss';
import { useModalNotification } from './use-modal-notification';

interface RequestModalProps {
  task: Task;
  onComplete?: () => void;
  closeModal: () => void;
}

const ClaimModal: FC<RequestModalProps> = ({ task, onComplete, closeModal }) => {
  const [loading, setLoading] = useState<boolean>(false);

  const [assignTask] = useMutation<{ assignTask: { status: UpdateStatus } }>(ASSIGN_TASK);
  const userProfile = useApplicationContext();

  const [renderSuccessNotification, renderErrorNotification] = useModalNotification(
    closeModal,
    'claim-modal-notification'
  );

  const handleFormSubmit = useCallback(async () => {
    setLoading(true);
    const userID = userProfile?.userId?.split('@')?.[0];
    try {
      const result = await assignTask({
        variables: {
          taskId: task.taskId,
          assignee: userID,
          id: userID,
        },
      });
      const errorMsg = result?.errors?.[0]?.extensions?.message;
      if (errorMsg) {
        throw new Error(errorMsg);
      }
      const status = result?.data?.assignTask?.status ?? '';
      if (status === 'OK') {
        onComplete?.();
        renderSuccessNotification(
          'The request is assigned to you',
          <p className="action-modal-notification">
            The request {task.taskNumber} has been assigned to you and moved to{' '}
            <span className="bold-text">My requests</span>.
          </p>
        );
      }
    } catch (e: any) {
      renderErrorNotification(
        'We’re unable to assign the request to you',
        <ErrorNotificationMessage errorMessage={e.message} />
      );
    } finally {
      setLoading(false);
    }
  }, [task.taskId]);

  return (
    <Form onSubmit={handleFormSubmit}>
      {({ formProps }) => (
        <div className="modal-form-wrapper" data-testid="claim-modal">
          <form {...formProps}>
            <FormHeader
              title={
                <div className="modal-title-wrapper">
                  <span className="modal-title">{ModalTitle.ASSIGN_TASK}</span>
                </div>
              }
            />
            <p>
              The request will be assigned to you, and this action will be added to the request in the Slack channel{' '}
              <SlackChannelLink url={task.slackMessageUrl}>{task.slackChannel}</SlackChannelLink> and Approval Desk.
            </p>
            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance="subtle" onClick={closeModal} testId="cancel-btn">
                  Cancel
                </Button>
                <LoadingButton appearance="primary" autoFocus type="submit" isLoading={loading} testId="submit-btn">
                  Assign to me
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          </form>
        </div>
      )}
    </Form>
  );
};

export default ClaimModal;
